import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from './ui/sidebar';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Home, Users, MessageSquare, LogOut, Settings, HelpCircle, MoreVertical, User2, CreditCard, Bell, PanelLeftOpen, BookOpen, Sun, Moon, Monitor, Mic, Phone } from 'lucide-react';
import { ThemeLogo } from './ThemeLogo';
import { useTheme } from '../contexts/ThemeContext';

interface ProtectedLayoutProps {
  children: React.ReactNode;
}

// Sidebar Header Component that uses useSidebar hook
const SidebarHeaderContent: React.FC = () => {
  const navigate = useNavigate();
  const { state, setOpen } = useSidebar();

  const handleLogoClick = () => {
    if (state === "open") {
      navigate('/');
    } else {
      // When collapsed, clicking the expand button opens the sidebar
      setOpen(true);
    }
  };

  return (
    <div className="flex items-center justify-between p-2">
      <div className="flex items-center cursor-pointer" onClick={handleLogoClick}>
        {state === "open" ? (
          <ThemeLogo />
        ) : (
          <PanelLeftOpen className="h-6 w-6" />
        )}
      </div>
      {/* Collapse button aligned with logo - only show when open */}
      {state === "open" && <SidebarTrigger className="h-6 w-6 p-1" />}
    </div>
  );
};

const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({ children }) => {
  const [userName, setUserName] = useState('');
  const [userPhotoURL, setUserPhotoURL] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Function to load user data from multiple sources
    const loadUserData = () => {
      // Try to get user data from authService first
      const user = authService.getCurrentUser();

      if (user && user.email) {
        setUserName(user.displayName || user.email?.split('@')[0] || 'User');
        setUserPhotoURL(user.photoURL || '');
        setUserEmail(user.email);
        return true; // Successfully loaded from authService
      }

      // Fallback: Try to get user data from localStorage
      const storedEmail = localStorage.getItem('userEmail');
      const storedName = localStorage.getItem('userName');
      const storedPhotoURL = localStorage.getItem('userPhotoURL');
      const storedUserInfo = localStorage.getItem('user_info');

      // Try parsed user_info first
      if (storedUserInfo) {
        try {
          const userInfo = JSON.parse(storedUserInfo);
          if (userInfo.email) {
            setUserEmail(userInfo.email);
            setUserName(userInfo.displayName || userInfo.email.split('@')[0] || 'User');
            setUserPhotoURL(userInfo.photoURL || '');
            return true;
          }
        } catch (error) {
        }
      }

      // Fallback to individual localStorage items
      if (storedEmail) {
        setUserEmail(storedEmail);
        setUserName(storedName || storedEmail.split('@')[0] || 'User');
        setUserPhotoURL(storedPhotoURL || '');
        return true;
      }

      return false;
    };

    // Load initial user data
    loadUserData();
  }, []);

  const handleSignOut = () => {
    setShowLogoutDialog(true);
  };

  const confirmSignOut = async () => {
    try {
      await authService.signOut();
      navigate('/');
    } catch (error) {
    }
    setShowLogoutDialog(false);
  };

  const navigationItems = [
    {
      title: "Home",
      url: "/dashboard",
      icon: Home,
    },
    {
      title: "Agents",
      url: "/agents",
      icon: Users,
    },
    {
      title: "Alpha Agent",
      url: "/alpha-agent",
      icon: Mic,
    },
    {
      title: "Knowledge base",
      url: "/knowledge-base",
      icon: BookOpen,
    },
    {
      title: "Conversations",
      url: "/conversations",
      icon: MessageSquare,
    },
    {
      title: "Phone Numbers",
      url: "/phone-numbers",
      icon: Phone,
    },
  ];

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <SidebarProvider defaultOpen={true}>

      <Sidebar variant="inset" collapsible="icon">
        <SidebarHeader>
          <SidebarHeaderContent />
        </SidebarHeader>
        {/* Separator under logo - full width without padding */}
        <Separator className="w-full" />
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Platform</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navigationItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton 
                      asChild 
                      isActive={isActive(item.url)}
                      className="cursor-pointer"
                    >
                      <div onClick={() => navigate(item.url)}>
                        <item.icon />
                        <span>{item.title}</span>
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarSeparator />
          <SidebarMenu>
            {/* Settings */}
            <SidebarMenuItem>
              <SidebarMenuButton>
                <Settings />
                <span>Settings</span>
              </SidebarMenuButton>
            </SidebarMenuItem>

            {/* Get Help */}
            <SidebarMenuItem>
              <SidebarMenuButton>
                <HelpCircle />
                <span>Get Help</span>
              </SidebarMenuButton>
            </SidebarMenuItem>


            {/* User Profile with Dropdown */}
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground focus:outline-none focus:ring-0 focus:ring-offset-0"
                  >
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage src={userPhotoURL} alt={userName} />
                      <AvatarFallback className="rounded-lg">
                        {userName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{userName || 'No Name'}</span>
                      <span className="truncate text-xs">{userEmail || 'No Email'}</span>
                    </div>
                    <MoreVertical className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-56 rounded-lg mb-2"
                  side="right"
                  align="end"
                  sideOffset={12}
                  alignOffset={-8}
                >
                  {/* User info at top of dropdown */}
                  <div className="flex items-center gap-3 p-3">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage src={userPhotoURL} alt={userName} />
                      <AvatarFallback className="rounded-lg">
                        {userName.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{userName || 'No Name'}</span>
                      <span className="truncate text-xs text-muted-foreground">{userEmail || 'No Email'}</span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-3 py-2 px-3">
                    <User2 className="h-4 w-4" />
                    <span>Account</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-3 py-2 px-3">
                    <CreditCard className="h-4 w-4" />
                    <span>Billing</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-3 py-2 px-3">
                    <Bell className="h-4 w-4" />
                    <span>Notifications</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="gap-3 py-2 px-3 focus:outline-none focus:ring-0 focus:bg-transparent focus:text-current">
                      <Sun className="h-4 w-4" />
                      <span>Appearance</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onClick={() => setTheme('light')} className="flex items-center">
                        <Sun className="h-4 w-4 mr-2" />
                        <span>Light</span>
                        {theme === 'light' && <span className="ml-auto text-xs">✓</span>}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTheme('dark')} className="flex items-center">
                        <Moon className="h-4 w-4 mr-2" />
                        <span>Dark</span>
                        {theme === 'dark' && <span className="ml-auto text-xs">✓</span>}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTheme('system')} className="flex items-center">
                        <Monitor className="h-4 w-4 mr-2" />
                        <span>System</span>
                        {theme === 'system' && <span className="ml-auto text-xs">✓</span>}
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="gap-3 py-2 px-3 text-destructive focus:text-destructive focus:bg-destructive/10">
                    <LogOut className="h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        {/* Page titles will be positioned here, above the separator */}
        <div className="flex flex-1 flex-col">
          {children}
        </div>
      </SidebarInset>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be signed out of your account and redirected to the login page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSignOut} className="bg-destructive text-destructive-foreground hover:bg-destructive/80">
              Logout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
};

export default ProtectedLayout;
